import React, { useState, useEffect } from 'react';
import { Tooltip, Modal } from 'antd';
import { ExternalLink } from 'lucide-react';
import { isEmpty } from 'lodash';
import { Storage } from 'aws-amplify';
import { CloudOutlined, VideoCameraOutlined, Html5Outlined } from '@ant-design/icons';

import { CreativeExpandedCard } from './CreativeExpandedCard';
import { formatWholeNumber } from '../../../../../../../core/utils/campaigns';
import DataTable from '../../../../../../shared/designSystem/components/DataTable';

// =============================================================================
// CreativeCTRTable — Display tab's Creatives table, now on TanStack Table v8
// via our shared <DataTable> primitive.
//
// v1 used a SimpleTable wrapper + inline styles + `footer` prop hack. This
// version uses DataTable's native summary row, expandable rows, and sorting,
// with columns defined declaratively. Thumbnail / dot / link all render as
// v2-styled cells using `.campaign-overview__creatives-*` classes.
// =============================================================================

const truncateUrl = (url, maxLength = 30) => {
  if (!url) return '';
  if (url.length <= maxLength) return url;
  return `${url.substring(0, maxLength)}...`;
};

// Media type guards — kept as no-ops matching the v1 logic. Fallbacks that
// were commented-out in the original stay commented-out here too; removing
// them entirely risks breaking attribution flows that rely on record.media.
const isVideoType = (_record) => false;
const isHTML5Type = (_record) => false;

const VideoPreviewCell = ({ s3VideoKey, onClick }) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchUrl = async () => {
      if (!s3VideoKey) return;
      try {
        const url = await Storage.get(s3VideoKey, { expires: 600000 });
        if (isMounted) {
          setVideoUrl(url);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching video URL:', error);
        if (isMounted) setLoading(false);
      }
    };
    fetchUrl();
    return () => { isMounted = false; };
  }, [s3VideoKey]);

  if (loading) {
    return (
      <div className="campaign-overview__creatives-thumb campaign-overview__creatives-thumb--loading">
        <VideoCameraOutlined style={{ fontSize: 20, color: '#999' }} />
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className="campaign-overview__creatives-thumb campaign-overview__creatives-thumb--video"
    >
      <video
        src={videoUrl}
        type="video/mp4"
        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
        muted
        autoPlay
        loop
      />
    </div>
  );
};

const VideoModal = ({ s3VideoKey, visible, onClose, videoName }) => {
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchUrl = async () => {
      if (!s3VideoKey) return;
      try {
        const url = await Storage.get(s3VideoKey, { expires: 600000 });
        if (isMounted) setVideoUrl(url);
      } catch (error) {
        console.error('Error fetching video URL for modal:', error);
      }
    };
    if (visible) fetchUrl();
    return () => { isMounted = false; };
  }, [s3VideoKey, visible]);

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width="80vw"
      centered
      closable={false}
      mask
      maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(3px)' }}
      maskClosable={false}
      style={{ maxWidth: 900, background: 'transparent', boxShadow: 'none' }}
      bodyStyle={{
        padding: '60px 0 40px 0',
        height: '70vh',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
      }}
      className="creative-preview-modal"
      destroyOnClose
    >
      <div
        style={{
          position: 'absolute',
          top: 10,
          left: 50,
          zIndex: 10,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.05))',
          color: 'white',
          padding: '6px 16px',
          borderRadius: 20,
          fontSize: 11,
          fontWeight: 600,
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.2)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          letterSpacing: '0.5px',
          maxWidth: 300,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {videoName || 'Video Unit'}
      </div>

      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 10,
          right: 50,
          zIndex: 10,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.05))',
          color: 'white',
          width: 32,
          height: 32,
          borderRadius: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 600,
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.2)',
          transition: 'all 0.2s ease',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }}
      >
        ×
      </div>

      <div
        style={{
          height: 'calc(70vh - 100px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0 60px',
        }}
      >
        {videoUrl && (
          <video
            controls
            autoPlay
            width="100%"
            height="100%"
            key={videoUrl}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              borderRadius: 8,
              boxShadow: '0 15px 40px rgba(0,0,0,0.4)',
              objectFit: 'contain',
            }}
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </div>
    </Modal>
  );
};

const CreativeCTRTable = ({ creativeData }) => {
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [selectedVideoKey, setSelectedVideoKey] = useState(null);
  const [selectedVideoName, setSelectedVideoName] = useState(null);

  // Does the dataset carry creativeUrl? Only then do we render the thumbnail
  // column — empty thumb col takes padding from every row for no reason.
  const hasThumbnailColumn =
    !isEmpty(creativeData) && creativeData[0] && creativeData[0].creativeUrl;

  // Dot column — subtle accent tying the row to its pie-slice color.
  const columns = [];
  columns.push({
    id: 'color',
    accessorKey: 'color',
    header: '',
    sortable: false,
    meta: { align: 'center', width: 24 },
    cell: ({ getValue }) => (
      <span
        className="campaign-overview__creatives-dot"
        style={{ backgroundColor: getValue() }}
        aria-hidden="true"
      />
    ),
  });

  if (hasThumbnailColumn) {
    columns.push({
      id: 'thumbnail',
      accessorKey: 'creativeUrl',
      header: '',
      sortable: false,
      meta: { align: 'left', width: 60 },
      cell: ({ row, getValue }) => {
        const record = row.original;
        const url = getValue();
        // Real S3 video previews hook through here once the type guards return
        // true again. For now: icon fallback + static image thumbnail.
        if (isVideoType(record) && record.s3VideoKey) {
          return (
            <VideoPreviewCell
              s3VideoKey={record.s3VideoKey}
              onClick={() => {
                setSelectedVideoKey(record.s3VideoKey);
                setSelectedVideoName(record.value || 'Video Creative');
                setVideoModalVisible(true);
              }}
            />
          );
        }
        let iconComponent = <CloudOutlined style={{ fontSize: 20, color: '#94a3b8' }} />;
        if (isVideoType(record))
          iconComponent = <VideoCameraOutlined style={{ fontSize: 20, color: '#94a3b8' }} />;
        else if (isHTML5Type(record))
          iconComponent = <Html5Outlined style={{ fontSize: 20, color: '#94a3b8' }} />;

        const hasImage = url && url !== '';
        return (
          <div
            className="campaign-overview__creatives-thumb"
            style={hasImage ? { backgroundImage: `url("${url}")` } : undefined}
          >
            {!hasImage && iconComponent}
          </div>
        );
      },
    });
  }

  columns.push({
    id: 'value',
    accessorKey: 'value',
    header: 'Name',
    sortable: true,
    meta: { align: 'left', width: 260 },
    cell: ({ getValue }) => {
      const text = getValue();
      if (!text) return <div />;
      if (text.length > 45) {
        return (
          <Tooltip overlayClassName="mjl-ds" title={text}>
            <span className="campaign-overview__creatives-name">
              {text.slice(0, 46)}…
            </span>
          </Tooltip>
        );
      }
      return <span className="campaign-overview__creatives-name">{text}</span>;
    },
  });

  columns.push({
    id: 'impressions',
    accessorKey: 'impressions',
    header: 'Impressions',
    sortable: true,
    meta: { align: 'right', width: 130 },
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? formatWholeNumber(v) : '-';
    },
  });

  columns.push({
    id: 'clicks',
    accessorKey: 'clicks',
    header: 'Clicks',
    sortable: true,
    meta: { align: 'right', width: 100 },
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? formatWholeNumber(v) : '-';
    },
  });

  columns.push({
    id: 'ctr',
    accessorKey: 'ctr',
    header: 'CTR',
    sortable: true,
    meta: { align: 'right', width: 90 },
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? `${v.toFixed(3)}%` : '-';
    },
  });

  columns.push({
    id: 'targetUrl',
    accessorKey: 'targetUrl',
    header: 'Click Through Link',
    sortable: false,
    meta: { align: 'left', width: 200 },
    cell: ({ getValue }) => {
      const url = getValue();
      if (!url) return <span className="campaign-overview__creatives-muted">-</span>;
      return (
        <Tooltip overlayClassName="mjl-ds" title={url}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="campaign-overview__creatives-link"
          >
            <ExternalLink size={13} strokeWidth={2} />
            <span>{truncateUrl(url, 25)}</span>
          </a>
        </Tooltip>
      );
    },
  });

  // Totals row — sum of Impressions + Clicks across ALL rows. CTR doesn't
  // sum meaningfully (per-creative ratio), so it shows '-' like targetUrl.
  const totalImpressions = creativeData.reduce(
    (sum, d) => sum + (parseFloat(d.impressions) || 0),
    0
  );
  const totalClicks = creativeData.reduce(
    (sum, d) => sum + (parseFloat(d.clicks) || 0),
    0
  );

  // Mobile card — same data as the table, stacked vertically. Thumbnail +
  // accent dot on the header, three metric tiles, link at the bottom.
  const renderMobileCard = (record) => {
    const hasThumb = record.creativeUrl && record.creativeUrl !== '';
    return (
      <>
        <div className="campaign-overview__creatives-card-head">
          {hasThumb ? (
            <div
              className="campaign-overview__creatives-thumb"
              style={{ backgroundImage: `url("${record.creativeUrl}")` }}
            />
          ) : (
            <div className="campaign-overview__creatives-thumb">
              <CloudOutlined style={{ fontSize: 20, color: '#94a3b8' }} />
            </div>
          )}
          <div className="campaign-overview__creatives-card-name">
            <span
              className="campaign-overview__creatives-dot"
              style={{ backgroundColor: record.color }}
              aria-hidden="true"
            />
            <span>{record.value || '—'}</span>
          </div>
        </div>

        <div className="campaign-overview__creatives-card-metrics">
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">Impressions</span>
            <span className="campaign-overview__creatives-card-metric-value">
              {record.impressions ? formatWholeNumber(record.impressions) : '-'}
            </span>
          </div>
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">Clicks</span>
            <span className="campaign-overview__creatives-card-metric-value">
              {record.clicks ? formatWholeNumber(record.clicks) : '-'}
            </span>
          </div>
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">CTR</span>
            <span className="campaign-overview__creatives-card-metric-value">
              {record.ctr ? `${record.ctr.toFixed(3)}%` : '-'}
            </span>
          </div>
        </div>

        {record.targetUrl && (
          <a
            href={record.targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="campaign-overview__creatives-link campaign-overview__creatives-card-link"
          >
            <ExternalLink size={13} strokeWidth={2} />
            <span>{truncateUrl(record.targetUrl, 40)}</span>
          </a>
        )}
      </>
    );
  };

  return (
    <>
      <DataTable
        data={creativeData}
        columns={columns}
        pageSize={10}
        mobileBreakpoint="md"
        renderMobileCard={renderMobileCard}
        expandable={{
          isRowExpandable: (record) => {
            const canExpand =
              (record.creativeUrl && !isVideoType(record) && !isHTML5Type(record)) ||
              (record.media && record.media.key);
            return canExpand;
          },
          renderExpandedRow: (record) =>
            record && <CreativeExpandedCard record={record} />,
        }}
        summaryRow={{
          labelColumnId: 'value',
          label: 'Totals + Averages',
          cells: {
            impressions: formatWholeNumber(totalImpressions),
            clicks: formatWholeNumber(totalClicks),
            ctr: '-',
            targetUrl: '-',
          },
        }}
        emptyState="No creatives match the current filter."
      />

      <VideoModal
        s3VideoKey={selectedVideoKey}
        videoName={selectedVideoName}
        visible={videoModalVisible}
        onClose={() => setVideoModalVisible(false)}
      />
    </>
  );
};

export default CreativeCTRTable;
